import { useEffect, useState } from "react";
import { Download, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, ts } from "@/lib/api";
import { PageHeader, Panel, Th, Td, btn } from "@/components/ui-kit";
import { SapUpload } from "@/components/sap/SapUpload";
import { SapPreview } from "@/components/sap/SapPreview";
import { SapResults } from "@/components/sap/SapResults";
import { OrderEditor, blankOrder } from "@/components/sap/OrderEditor";
import { SftpPanel } from "@/components/sap/SftpPanel";
import { SapChecker } from "@/components/sap/SapChecker";
import { CoupaPanel } from "@/components/sap/CoupaPanel";
import { NonEdiOrders } from "@/components/sap/NonEdiOrders";
import { HaviPanel } from "@/components/sap/HaviPanel";
import { McdPanel } from "@/components/sap/McdPanel";
import { CasinoPanel } from "@/components/sap/CasinoPanel";
import { ShorrPanel } from "@/components/sap/ShorrPanel";
import { DelhaizePanel } from "@/components/sap/DelhaizePanel";
import { SapDeliveriesPanel } from "@/components/sap/SapDeliveriesPanel";

const template = async () => {
  const r = await api.get("/sap/template", { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = "SAP_Amazon_Order_Import_Template.csv";
  a.click();
};

const ManualEntry = () => {
  const [o, setO] = useState(blankOrder());
  const [res, setRes] = useState(null);
  const save = async () => {
    try {
      const r = (await api.post("/sap/create", { orders: [o] })).data.results;
      setRes(r);
      r[0].status === "error" || r[0].status === "duplicate" ? toast.error(r[0].message) : (toast.success("Order created"), setO(blankOrder()));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4">
      <Panel title="Manual Amazon order" testId="manual-order-panel">
        <div className="space-y-4 p-5">
          <OrderEditor o={o} set={setO} prefix="manual" />
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" className={btn.ghost} onClick={() => setO({ ...o, lines: [...o.lines, { line_no: String((o.lines.length + 1) * 10), material: "", customer_material: "", description: "", qty: "", uom: "RL" }] })} data-testid="manual-add-line-btn"><Plus className="mr-1.5 h-4 w-4" /> Add line</Button>
            {o.lines.length > 1 && <Button variant="ghost" className={btn.danger} onClick={() => setO({ ...o, lines: o.lines.slice(0, -1) })} data-testid="manual-remove-line-btn"><Trash2 className="mr-1.5 h-4 w-4" /> Remove last line</Button>}
            <Button onClick={save} className={`ml-auto ${btn.primary}`} data-testid="manual-create-btn">Create order</Button>
          </div>
        </div>
      </Panel>
      {res && <SapResults results={res} />}
    </div>
  );
};

const FieldMap = () => {
  const [f, setF] = useState([]);
  useEffect(() => { api.get("/sap/fields").then((r) => setF(r.data)); }, []);
  return (
    <Panel title="SAP ECC 6.0 field mapping (give this to your SAP team)" testId="sap-field-map">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-slate-800"><tr><Th>Column</Th><Th>SAP ECC source</Th><Th>Req.</Th><Th>Description</Th><Th>Example</Th></tr></thead>
          <tbody className="divide-y divide-slate-800/70">
            {f.map((x) => (
              <tr key={x.column} data-testid={`sap-field-${x.column}`}>
                <Td className="font-mono text-amber-300">{x.column}</Td><Td className="font-mono text-xs">{x.sap_source}</Td>
                <Td>{x.required ? <span className="text-rose-300">Yes</span> : "—"}</Td><Td className="text-xs">{x.description}</Td><Td className="font-mono text-xs text-slate-400">{x.example}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

const History = ({ rows }) => (
  <Panel title="Recent imports" testId="sap-history">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-slate-800"><tr><Th>When</Th><Th>File</Th><Th>Rows</Th><Th>Status</Th><Th>By</Th></tr></thead>
        <tbody className="divide-y divide-slate-800/70">
          {rows.map((b) => <tr key={b.id}><Td className="font-mono text-slate-500">{ts(b.created_at)}</Td><Td>{b.filename}</Td><Td>{b.rows}</Td><Td className="font-mono text-xs uppercase">{b.status}{b.error && <span className="ml-2 normal-case text-rose-300">{b.error}</span>}</Td><Td className="text-xs">{b.uploaded_by}</Td></tr>)}
        </tbody>
      </table>
    </div>
  </Panel>
);

export default function SapImport() {
  const [batch, setBatch] = useState(null);
  const [hist, setHist] = useState([]);
  const load = () => api.get("/sap/batches").then((r) => setHist(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4">
      <PageHeader title="Customer order import" subtitle="Amazon sends its POs to Euro Packaging. EP keys the sales order into SAP and the order reaches the Hub from an SAP export (upload or SFTP), a Coupa PO (cXML push or export upload) or manual entry. Each order releases its 940 to Group O automatically. Duplicates (same Amazon PO or SAP sales order) are skipped and flagged.">
        <Button variant="ghost" onClick={template} className={btn.ghost} data-testid="sap-template-btn"><Download className="mr-1.5 h-4 w-4" /> Import template (CSV)</Button>
      </PageHeader>
      <Tabs defaultValue="upload">
        <TabsList data-testid="sap-tabs" className="flex h-auto flex-wrap justify-start">
          <TabsTrigger value="upload" data-testid="sap-tab-upload">SAP file upload</TabsTrigger>
          <TabsTrigger value="coupa" data-testid="sap-tab-coupa">Coupa</TabsTrigger>
          <TabsTrigger value="manual" data-testid="sap-tab-manual">Manual entry</TabsTrigger>
          <TabsTrigger value="noedi" data-testid="sap-tab-noedi">Customers without EDI</TabsTrigger>
          <TabsTrigger value="havi" data-testid="sap-tab-havi">HAVI / McDonald's</TabsTrigger>
          <TabsTrigger value="mcd" data-testid="sap-tab-mcd">McDonald's distributor POs</TabsTrigger>
          <TabsTrigger value="shorr" data-testid="sap-tab-shorr">Shorr (USA)</TabsTrigger>
          <TabsTrigger value="casino" data-testid="sap-tab-casino">Casino (EDIFACT)</TabsTrigger>
          <TabsTrigger value="delhaize" data-testid="sap-tab-delhaize">Delhaize (EDIFACT)</TabsTrigger>
          <TabsTrigger value="check" data-testid="sap-tab-check">Test file checker</TabsTrigger>
          <TabsTrigger value="fields" data-testid="sap-tab-fields">SAP field map</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="sap-tab-deliveries">SAP deliveries (ASN)</TabsTrigger>
          <TabsTrigger value="sftp" data-testid="sap-tab-sftp">SFTP & SAP status</TabsTrigger>
        </TabsList>
        <TabsContent value="upload" className="space-y-4">
          <SapUpload onPreview={(b) => { setBatch(b); load(); }} />
          {batch && <SapPreview key={batch.id} batch={batch} onDone={load} />}
          {hist.length > 0 && <History rows={hist} />}
        </TabsContent>
        <TabsContent value="coupa"><CoupaPanel onImported={load} /></TabsContent>
        <TabsContent value="manual"><ManualEntry /></TabsContent>
        <TabsContent value="noedi"><NonEdiOrders /></TabsContent>
        <TabsContent value="havi"><HaviPanel /></TabsContent>
        <TabsContent value="mcd"><McdPanel /></TabsContent>
        <TabsContent value="shorr"><ShorrPanel /></TabsContent>
        <TabsContent value="casino"><CasinoPanel /></TabsContent>
        <TabsContent value="delhaize"><DelhaizePanel /></TabsContent>
        <TabsContent value="check"><SapChecker /></TabsContent>
        <TabsContent value="fields"><FieldMap /></TabsContent>
        <TabsContent value="deliveries"><SapDeliveriesPanel /></TabsContent>
        <TabsContent value="sftp"><SftpPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
